import React, { useEffect } from 'react'
import { object, string } from 'yup'
import { Grid } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import { QrFormProps } from '../../qrs.types.ts'
import {
  Form,
  FormSchemaMessages,
  FormSchemaTests,
  RadioGroupControl,
  SubmitButton,
  TextFieldControl,
} from '../../../../components/form'
import { NewStaticC2BFormState } from '../../qrs.types.ts'
import { Dialog, useSnackbarContext } from '../../../../components'
import { MerchantField } from '../form-fields/merchant-field.tsx'
import { useMutateRegisterC2B } from '../../../../services'
import { omitRequestParams, preventHandler } from '../../../../utils.ts'
import { RegisterStaticC2BQrParams } from '../../../../types.ts'

import { StaticQrAmountField } from './static-qr-amount-field.tsx'
import { useAppSelector } from '@/stores/hooks.ts'

const newC2BStaticValidationSchema = object<NewStaticC2BFormState>({
  merchantId: string().required(FormSchemaMessages.required),
  reference: string(),
  amount: string().when(['amountType'], ([amountType], schema) =>
    amountType === 'fixed'
      ? schema.test(FormSchemaTests.isWithinRange(10, 1000000)).required(FormSchemaMessages.required)
      : schema
  ),
  purpose: string(),
})

const C2B_STATIC_FORM_NAME = 'newStaticC2BForm'

export const NewStaticC2BForm = ({ open, onClose }: QrFormProps): React.ReactElement => {
  const tspList = useAppSelector((state) => state.user.user?.tspList ?? [])
  const { addSnack } = useSnackbarContext()

  const qc = useQueryClient()
  const { mutateAsync, status, isPending, data, reset } = useMutateRegisterC2B()

  const handleCreateStaticC2BSubmit = async ({ amountType, amount, ...formState }: NewStaticC2BFormState) => {
    await preventHandler(
      mutateAsync,
      isPending
    )(
      omitRequestParams<RegisterStaticC2BQrParams>({
        ...formState,
        amount: Number(amount),
      })
    )
  }

  useEffect(() => {
    switch (status) {
      case 'success': {
        const response = data?.data
        const isSuccess = response.state === 'REGISTERED'
        const errorMessage =
          response.state === 'REJECTED' && 'errorMessage' in response
            ? response?.errorMessage ?? 'Произошла ошибка при создании QR, попробуйте ещё раз'
            : ''

        addSnack({
          message: isSuccess ? 'Заявка на создание статического QR C2B успешно отправлена' : errorMessage,
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })

        if (isSuccess) {
          qc.invalidateQueries({ queryKey: ['C2B_list'] })
          onClose()
          reset()
        }

        break
      }

      case 'error': {
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        break
      }

      default:
        break
    }
  }, [status])

  return (
    <Form<NewStaticC2BFormState>
      validationSchema={newC2BStaticValidationSchema}
      name={C2B_STATIC_FORM_NAME}
      onSubmit={handleCreateStaticC2BSubmit}
      initialValues={{
        merchantId: tspList[0]?.merchantId,
        qrcType: '01',
        amountType: 'fixed',
      }}
    >
      <Dialog
        title="Заявка на статический C2B QR"
        open={open}
        maxWidth="md"
        onClose={onClose}
        content={
          <Grid container flexDirection="column" spacing={2}>
            <Grid item xs={12}>
              <MerchantField />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewStaticC2BFormState> name="reference" fullWidth label="№ заказа в магазине" />
            </Grid>
            <Grid item>
              <RadioGroupControl<NewStaticC2BFormState>
                name="amountType"
                revalidateFields={['amount']}
                row
                label="Вид QR"
                items={[
                  { title: 'Фиксированная сумма', value: 'fixed' },
                  { title: 'Открытый номинал (сумма вводится клиентом)', value: 'open' },
                ]}
              />
            </Grid>
            <StaticQrAmountField />
            <Grid item xs={12}>
              <TextFieldControl<NewStaticC2BFormState> name="purpose" fullWidth label="Назначение платежа" />
            </Grid>
          </Grid>
        }
        actions={<SubmitButton form={C2B_STATIC_FORM_NAME}>Отправить заявку</SubmitButton>}
      />
    </Form>
  )
}
